import React from 'react';
import type { ClickAwayListenerProps as MuiClickAwayListenerProps } from '@mui/material';
import { ClickAwayListener as MuiClickAwayListener } from '@mui/material';
import { isEmpty } from 'lodash';

import { reportErrorInProductionOrThrow } from '~/Utils';

/**
 * Explicit ClickAwayListener props based on actual usage in the codebase.
 *
 * A plain function component rather than `forwardRef`: MUI's listener renders no element of its own and takes
 * no ref — it attaches one to its child, which is the node a click must land outside of.
 */
export interface ClickAwayListenerProps {
  /** The element a click must land outside of. It must be able to hold a ref. */
  children: MuiClickAwayListenerProps['children'];
  /** Called on a click outside `children`, including outside any portal rendered from it. */
  onClickAway: MuiClickAwayListenerProps['onClickAway'];
  /** The mouse event to listen to, or `false` to stop listening. */
  mouseEvent?: MuiClickAwayListenerProps['mouseEvent'];
  /** The touch event to listen to, or `false` to stop listening. */
  touchEvent?: MuiClickAwayListenerProps['touchEvent'];
}

/**
 * This Component is a wrapper on MUI component.
 * This component should be imported and not MUI directly!
 */
const ClickAwayListener: React.FC<ClickAwayListenerProps> = ({
  children,
  onClickAway,
  mouseEvent,
  touchEvent,
  ...restProps
}) => {
  if (!isEmpty(restProps)) {
    reportErrorInProductionOrThrow(
      `Unexpected props passed to ClickAwayListener: ${Object.keys(restProps).join(', ')}.
Examine if these need to be removed, or added explicitly to the ClickAwayListenerProps interface and to the component's signature.`,
      true
    );
  }
  return (
    <MuiClickAwayListener onClickAway={onClickAway} mouseEvent={mouseEvent} touchEvent={touchEvent} {...restProps}>
      {children}
    </MuiClickAwayListener>
  );
};

ClickAwayListener.displayName = 'ClickAwayListener';

export default ClickAwayListener;
